import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

/**
 * @param {string} userId
 * @returns {import('firebase/firestore').CollectionReference}
 */
function companiesCol(userId) {
  return collection(firestore, 'users', userId, 'companies');
}

/**
 * Subscribe to the current user's companies (subcollection).
 * @param {string} userId
 * @param {(items: Array<{ id: string } & object>) => void} callback
 * @returns {() => void}
 */
export function subscribeUserCompanies(userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  return onSnapshot(
    companiesCol(userId),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => String(a.legalName || '').localeCompare(String(b.legalName || ''), undefined, { sensitivity: 'base' }));
      callback(items);
    },
    (err) => {
      console.warn('[userCompanies] snapshot error:', err?.code, err?.message);
      callback([]);
    }
  );
}

function trimOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

/**
 * @param {string} userId
 * @param {{
 *   legalName: string,
 *   address?: string | null,
 *   addressProvince?: string | null,
 *   federalBusinessNumber?: string | null,
 *   provincialBusinessNumber?: string | null,
 * }} data
 * @returns {Promise<string>} company doc id
 */
export async function addUserCompany(userId, data) {
  if (!userId) throw new Error('userId required');
  const legalName = (data?.legalName || '').trim();
  if (!legalName) throw new Error('legalName required');
  const ref = await addDoc(companiesCol(userId), {
    legalName,
    address: trimOrNull(data?.address),
    addressProvince: trimOrNull(data?.addressProvince)?.toUpperCase() || null,
    federalBusinessNumber: trimOrNull(data?.federalBusinessNumber),
    provincialBusinessNumber: trimOrNull(data?.provincialBusinessNumber),
    neq: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * @param {string} userId
 * @param {string} companyId
 * @param {{
 *   legalName?: string,
 *   address?: string | null,
 *   addressProvince?: string | null,
 *   federalBusinessNumber?: string | null,
 *   provincialBusinessNumber?: string | null,
 * }} data
 */
export async function updateUserCompany(userId, companyId, data) {
  if (!userId || !companyId) throw new Error('userId and companyId required');
  const ref = doc(firestore, 'users', userId, 'companies', companyId);
  const payload = { updatedAt: serverTimestamp() };
  if (data.legalName != null) payload.legalName = String(data.legalName).trim();
  if (data.address !== undefined) payload.address = trimOrNull(data.address);
  if (data.addressProvince !== undefined) {
    payload.addressProvince = trimOrNull(data.addressProvince)?.toUpperCase() || null;
  }
  if (data.federalBusinessNumber !== undefined) {
    payload.federalBusinessNumber = trimOrNull(data.federalBusinessNumber);
  }
  if (data.provincialBusinessNumber !== undefined) {
    payload.provincialBusinessNumber = trimOrNull(data.provincialBusinessNumber);
  }
  if (
    data.addressProvince !== undefined ||
    data.federalBusinessNumber !== undefined ||
    data.provincialBusinessNumber !== undefined
  ) {
    payload.neq = null;
  }
  await updateDoc(ref, payload);
}

/**
 * @param {string} userId
 * @param {string} companyId
 */
export async function deleteUserCompany(userId, companyId) {
  if (!userId || !companyId) throw new Error('userId and companyId required');
  await deleteDoc(doc(firestore, 'users', userId, 'companies', companyId));
}
